/**
 * Color Scale Generator
 *
 * For default brand colors: returns exact hardcoded values from theme.css
 * For custom colors: generates 50-900 scale via sRGB mixing (JS equivalent of CSS color-mix)
 */

import { generateScale, type ThemeMode } from './color'

export type ColorScaleStep = 25 | 50 | 100 | 150 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900
export type ColorScale = Partial<Record<ColorScaleStep, string>>

// Default brand hex values from theme.css light mode (:root)
export const DEFAULT_BRAND_HEX: Record<string, string> = {
  primary: '#004eeb',
  tertiary: '#004eeb',
  font: '#101828',
  border: '#eaecf0',
  error: '#d92d20',
  warning: '#f79009',
  background: '#ffffff',
  surface: '#f5f6f8',
  success: '#039855',
  sidebar: '#ffffff',
}

// Hand-tuned scales from theme.css that DON'T follow the color-mix formula
const FULL_HARDCODED_SCALES: Record<string, ColorScale> = {
  primary: {
    25: '#f5f8ff',
    50: '#eff4ff',
    100: '#dbeafe',
    200: '#b2ccff',
    300: '#93c5fd',
    400: '#528bff',
    500: '#004eeb',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  tertiary: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#004eeb',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  font: {
    50: '#f3f4f6',
    100: '#f2f4f7',
    200: '#d1d5db',
    300: '#9ca3af',
    400: '#98a2b3',
    500: '#667085',
    600: '#475467',
    700: '#344054',
    800: '#1d2939',
    900: '#101828',
  },
  // Default when sidebar is #ffffff (theme.css :root sidebar scale)
  sidebar: {
    25: '#ffffff',
    50: '#ffffff',
    100: '#ffffff',
    200: '#ffffff',
    300: '#ffffff',
    400: '#ffffff',
    500: '#ffffff',
    600: '#eaecf0',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
}

// Partial overrides for scales that are mostly color-mix but have a few hand-tuned values
const PARTIAL_HARDCODED_OVERRIDES: Record<string, ColorScale> = {
  error: { 300: '#fda29b', 700: '#b42318' },
  border: { 600: '#d0d5dd' },
  success: { 100: '#ecfdf3', 150: '#cffae6', 800: '#027a48' },
}

/**
 * Returns a 50-900 color scale for a given hex value.
 *
 * @param hex - The base color in hex format (e.g. '#8B5CF6')
 * @param tokenName - Optional token name (e.g. 'primary', 'error') to look up hardcoded defaults
 * @returns A record of scale step -> hex value
 */
export function getColorScale(hex: string, tokenName?: string, mode: ThemeMode = 'light'): ColorScale {
  const normalizedHex = hex.toLowerCase().trim()

  // If this is a default brand color with a full hardcoded scale, return it directly
  if (tokenName && FULL_HARDCODED_SCALES[tokenName] && DEFAULT_BRAND_HEX[tokenName] === normalizedHex) {
    return { ...FULL_HARDCODED_SCALES[tokenName] }
  }

  // Generate scale programmatically via sRGB mixing
  const generated = generateScale(normalizedHex, mode)

  // Convert Record<number, string> to ColorScale
  const scale: ColorScale = {}
  for (const [step, value] of Object.entries(generated)) {
    scale[Number(step) as ColorScaleStep] = value
  }

  // If default brand color with partial overrides, merge the hand-tuned values
  if (tokenName && PARTIAL_HARDCODED_OVERRIDES[tokenName] && DEFAULT_BRAND_HEX[tokenName] === normalizedHex) {
    return { ...scale, ...PARTIAL_HARDCODED_OVERRIDES[tokenName] }
  }

  return scale
}

/**
 * Applies a color scale to CSS custom properties on an element.
 *
 * @param root - The target element (usually document.documentElement)
 * @param tokenPrefix - CSS variable prefix (e.g. '--cp-primary')
 * @param scale - The color scale to apply
 */
export function applyColorScale(root: HTMLElement, tokenPrefix: string, scale: ColorScale) {
  for (const [step, value] of Object.entries(scale)) {
    if (value) {
      root.style.setProperty(`${tokenPrefix}-${step}`, value)
    }
  }
}

/**
 * Removes a color scale from CSS custom properties (resets to CSS-defined values).
 */
export function removeColorScale(root: HTMLElement, tokenPrefix: string) {
  const steps: ColorScaleStep[] = [25, 50, 100, 150, 200, 300, 400, 500, 600, 700, 800, 900]
  for (const step of steps) {
    root.style.removeProperty(`${tokenPrefix}-${step}`)
  }
}
